import React, { KeyboardEvent, useEffect } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { EGG_KNOWN_ACTIONS } from "../../types";

export const AVAILABLE_SCOPES = ["webhook:egg", "lead-dispatch"] as const;
export type WebhookKeyScope = (typeof AVAILABLE_SCOPES)[number];

const formSchema = z.object({
  partnerName: z
    .string()
    .min(1, "Campo obbligatorio")
    .max(120, "Massimo 120 caratteri"),
  scopes: z
    .array(z.enum(AVAILABLE_SCOPES))
    .min(1, "Seleziona almeno uno scope"),
  allowedEventTypes: z.array(z.string()).optional(),
  notes: z.string().max(500, "Massimo 500 caratteri").optional().or(z.literal("")),
});

export type WebhookKeyFormData = z.infer<typeof formSchema>;

interface Props {
  mode: "create" | "edit";
  defaultValues?: Partial<WebhookKeyFormData>;
  onSubmit: (data: WebhookKeyFormData) => Promise<void>;
  isLoading: boolean;
  submitLabel: string;
  submitError?: string | null;
  disabled?: boolean;
}

// Fonte unica condivisa con le regole EGG (src/types) per evitare liste divergenti.
const EVENT_TYPE_SUGGESTIONS = [...EGG_KNOWN_ACTIONS];

export default function WebhookKeyForm({
  mode,
  defaultValues,
  onSubmit,
  isLoading,
  submitLabel,
  submitError,
  disabled = false,
}: Props) {
  const {
    control,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm<WebhookKeyFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      partnerName: "",
      scopes: ["webhook:egg"],
      allowedEventTypes: [],
      notes: "",
      ...defaultValues,
    },
  });

  useEffect(() => {
    if (defaultValues) {
      reset({
        partnerName: "",
        scopes: ["webhook:egg"],
        allowedEventTypes: [],
        notes: "",
        ...defaultValues,
      });
    }
  }, [defaultValues, reset]);

  const allowedEventTypes = watch("allowedEventTypes") ?? [];
  const selectedScopes = watch("scopes") ?? [];

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
        <Controller
          name="partnerName"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Nome partner"
              required
              fullWidth
              disabled={disabled}
              placeholder="es. EGG Finance prod"
              error={!!errors.partnerName}
              helperText={
                errors.partnerName?.message ??
                (mode === "create"
                  ? "Nome leggibile del partner. Il Partner ID tecnico viene generato automaticamente."
                  : undefined)
              }
            />
          )}
        />

        {mode === "create" && (
          <Box>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
              Scope della chiave
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 1 }}
            >
              Determina quali endpoint la chiave può chiamare. Una chiave può
              avere più scope.
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {AVAILABLE_SCOPES.map((scope) => {
                const checked = selectedScopes.includes(scope);
                return (
                  <Chip
                    key={scope}
                    label={scope}
                    color={checked ? "primary" : "default"}
                    variant={checked ? "filled" : "outlined"}
                    onClick={() => {
                      const next = checked
                        ? selectedScopes.filter((s) => s !== scope)
                        : [...selectedScopes, scope];
                      setValue("scopes", next as WebhookKeyScope[], {
                        shouldValidate: true,
                      });
                    }}
                    sx={{ fontFamily: "monospace", fontSize: "0.72rem" }}
                  />
                );
              })}
            </Stack>
            {errors.scopes && (
              <Typography variant="caption" color="error" sx={{ display: "block", mt: 0.5 }}>
                {errors.scopes.message}
              </Typography>
            )}
          </Box>
        )}

        <Box>
          <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
            Event types ammessi (opzionale)
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
            Se vuoto la chiave è valida per tutti i tipi di evento. Premi Invio o virgola.
          </Typography>
          <Autocomplete
            multiple
            freeSolo
            disabled={disabled}
            options={EVENT_TYPE_SUGGESTIONS}
            value={allowedEventTypes}
            onChange={(_, newValue) =>
              setValue("allowedEventTypes", newValue as string[])
            }
            onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
              if (e.key === "," || e.key === "Enter") {
                const input = (e.target as HTMLInputElement).value?.trim();
                if (input && !allowedEventTypes.includes(input)) {
                  setValue("allowedEventTypes", [...allowedEventTypes, input]);
                  (e.target as HTMLInputElement).value = "";
                }
                if (e.key === ",") e.preventDefault();
              }
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option}
                  label={option}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ fontFamily: "monospace", fontSize: "0.72rem" }}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={allowedEventTypes.length === 0 ? "es. lead.create.pre" : ""}
                size="small"
              />
            )}
          />
        </Box>

        <Controller
          name="notes"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Note"
              multiline
              minRows={3}
              maxRows={6}
              fullWidth
              disabled={disabled}
              placeholder="Note interne sull'utilizzo della chiave..."
              inputProps={{ maxLength: 500 }}
              error={!!errors.notes}
              helperText={
                errors.notes?.message ??
                `${(field.value ?? "").length} / 500`
              }
            />
          )}
        />

        {submitError && <Alert severity="error">{submitError}</Alert>}

        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading || disabled}
          >
            {isLoading ? "Salvataggio..." : submitLabel}
          </Button>
        </Box>
      </Box>
    </form>
  );
}
